import React, { useContext } from "react";
import { Box, Link as MuiLink, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import {
  KivuI18nContext,
  localizedPath,
  PAGE_ROUTE_CONTACT,
  PAGE_ROUTE_USER_GUIDE,
  useKivunovaTranslation,
  PAGE_ROUTE_ABOUT_PRODUCT,
  PAGE_ROUTE_LEARN_MORE_CATEGORIES,
  PAGE_ROUTE_ADD_PRODUCT_SOP,
  PAGE_ROUTE_SOPS
} from "@kivunova/kivufrontendcommon";
import PublicInfoCallout from "./PublicInfoCallout";
import PublicInfoCtaPanel from "./PublicInfoCtaPanel";
import PublicInfoPageHeader from "./PublicInfoPageHeader";
import PublicMarketingLayout from "./PublicMarketingLayout";
import { ADD_CATEGORY_SOP_SECTIONS } from "./constants/addCategorySopContent";
import { ADD_CATEGORY_SOP_IMAGES } from "./constants/addCategorySopImages";
import type { AddCategorySopImageConfig } from "./types";
import {
  publicInfoBodySx,
  publicInfoGuideSectionSx,
  publicInfoLinkSx,
  publicInfoOrderedListSx,
  publicInfoSectionTitleWithImageSx,
  userGuideSectionImageSx
} from "./constants/publicInfoPageStyles";

const AddCategorySopImage: React.FC<{
  image: AddCategorySopImageConfig;
  testId: string;
}> = ({ image, testId }) => {
  const { t } = useKivunovaTranslation();

  return (
    <Box
      component="img"
      src={image.src}
      alt={t(image.altKey, image.altFallback)}
      data-testid={testId}
      sx={userGuideSectionImageSx}
    />
  );
};

const AddCategorySopPage: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const aboutProductPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_ABOUT_PRODUCT, language, defaultLang)
      : `/${PAGE_ROUTE_ABOUT_PRODUCT}`;
  const categoriesLearnMorePath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_LEARN_MORE_CATEGORIES, language, defaultLang)
      : `/${PAGE_ROUTE_LEARN_MORE_CATEGORIES}`;
  const sopsHubPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_SOPS, language, defaultLang)
      : `/${PAGE_ROUTE_SOPS}`;
  const addProductSopPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_ADD_PRODUCT_SOP, language, defaultLang)
      : `/${PAGE_ROUTE_ADD_PRODUCT_SOP}`;
  const userGuidePath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_USER_GUIDE, language, defaultLang)
      : `/${PAGE_ROUTE_USER_GUIDE}`;
  const contactPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_CONTACT, language, defaultLang)
      : `/${PAGE_ROUTE_CONTACT}`;

  return (
    <PublicMarketingLayout>
      <PublicInfoPageHeader
        pageTestId="add-category-sop-page"
        eyebrow={t("add-category-sop-page-eyebrow", "Run books")}
        title={t("add-category-sop-page-title", "How to add a new category")}
        intro={t(
          "add-category-sop-page-intro",
          "Group your products into clear categories so your team can find, sell, and manage stock more easily."
        )}
      />

      <PublicInfoCallout testId="add-category-sop-page-note">
        {t(
          "add-category-sop-page-note",
          "You need permission to manage categories. If you do not see the Categories menu or the Add New Category button, ask your company admin."
        )}
      </PublicInfoCallout>

      <Stack spacing={4} sx={{ mt: 3 }}>
        {ADD_CATEGORY_SOP_SECTIONS.map((section, sectionIndex) => (
          <Box
            key={section.id}
            component="section"
            sx={publicInfoGuideSectionSx}
            data-testid={`add-category-sop-section-${section.id}`}
          >
            <Typography component="h2" sx={publicInfoSectionTitleWithImageSx}>
              {sectionIndex + 1}. {t(section.titleKey, section.titleFallback)}
            </Typography>
            <Box component="ol" sx={publicInfoOrderedListSx}>
              {section.steps.map((step, stepIndex) => {
                const image =
                  step.imageId != null
                    ? ADD_CATEGORY_SOP_IMAGES[step.imageId]
                    : undefined;

                return (
                  <Box key={`${section.id}-${stepIndex}`} component="li">
                    <Typography
                      component="span"
                      variant="body1"
                      sx={publicInfoBodySx}
                    >
                      {t(step.bodyKey, step.bodyFallback)}
                    </Typography>
                    {image ? (
                      <AddCategorySopImage
                        image={image}
                        testId={`add-category-sop-image-${step.imageId}`}
                      />
                    ) : null}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Stack>

      <PublicInfoCtaPanel
        title={t(
          "add-category-sop-page-cta-title",
          "Category saved. What next?"
        )}
        testId="add-category-sop-page-cta"
      >
        <Typography variant="body1" sx={publicInfoBodySx}>
          {t(
            "add-category-sop-page-cta-body",
            "Add products and assign each one to the right category. Then record stock with product items."
          )}{" "}
          <MuiLink
            component={Link}
            to={sopsHubPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("add-category-sop-page-cta-all-guides-link", "Run books")}
          </MuiLink>
          {t("add-category-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={addProductSopPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t(
              "add-category-sop-page-cta-add-product-link",
              "How to add a product"
            )}
          </MuiLink>
          {t("add-category-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={categoriesLearnMorePath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t(
              "add-category-sop-page-cta-categories-link",
              "Categories and subcategories"
            )}
          </MuiLink>
          {t("add-category-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={aboutProductPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t(
              "add-category-sop-page-cta-about-product-link",
              "Products vs. product items"
            )}
          </MuiLink>
          {t("add-category-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={userGuidePath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("add-category-sop-page-cta-guide-link", "User guide")}
          </MuiLink>
          {t("add-category-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={contactPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("add-category-sop-page-cta-contact-link", "Contact us")}
          </MuiLink>
        </Typography>
      </PublicInfoCtaPanel>
    </PublicMarketingLayout>
  );
};

export default AddCategorySopPage;
